import React, { useEffect, useMemo } from 'react';

import { ConciergeVariables } from './concierge-variables';
import { buildConciergeOverridesCss, conciergeThemeAttribute, ConciergeCustomizationSettings } from './concierge-theme';
import { getConciergePortalContainer } from './concierge-portal-container';

type ConciergeProvidersProps = {
  children: React.ReactNode;
  customization?: ConciergeCustomizationSettings | null;
  isKiosk?: boolean;
};

const CONCIERGE_MODE_CLASS = 'concierge-mode';
const CONCIERGE_THEME_ATTR = 'data-concierge-theme';

export const ConciergeProviders = ({
  children,
  customization,
  isKiosk = false,
}: ConciergeProvidersProps): JSX.Element => {
  // The .concierge-mode class scopes Tier 2b token overrides, and the
  // data-concierge-theme attribute opts into the light or light-max-contrast
  // variant — derived from the canvas color, not stored. Dark is the base
  // layer and deliberately has no attribute value or selector (adding one
  // would raise dark's specificity from (0,1,0) to (0,2,0) and tie it with
  // light).
  //
  // On web both go on <body>, so host MUI portals (Drawer, Modal, Popover) —
  // which render outside the React tree at document.body — also inherit the
  // Concierge token cascade. On kiosk the host chrome around the Concierge
  // (cart drawer, menu-type list, sidebar) keeps the store colors, so <body>
  // stays unthemed: the mount (ConciergeHost) and the idle warning carry the
  // scope themselves, and this themes the remote's portal container.
  const themeAttribute = conciergeThemeAttribute(customization);

  useEffect(() => {
    const target = isKiosk ? getConciergePortalContainer() : document.body;
    target.classList.add(CONCIERGE_MODE_CLASS);
    if (themeAttribute) {
      target.setAttribute(CONCIERGE_THEME_ATTR, themeAttribute);
    }
    return () => {
      target.classList.remove(CONCIERGE_MODE_CLASS);
      target.removeAttribute(CONCIERGE_THEME_ATTR);
    };
  }, [isKiosk, themeAttribute]);

  const tenantOverrides = useMemo(() => buildConciergeOverridesCss(customization), [customization]);

  return (
    <>
      <ConciergeVariables $tenantOverrides={tenantOverrides} />
      {children}
    </>
  );
};
